import {
  communityToChoice,
  userNotLoggedInOrBanned,
  searchCommunities,
  filterCommunitySelection,
} from "@utils/app";
import {
  capitalizeFirstLetter,
  debounce,
  getIdFromString,
  getQueryString,
  validTitle,
  validURL,
} from "@utils/helpers";
import { isImage, isMedia } from "@utils/media";
import { Choice, StringBoolean } from "@utils/types";
import autosize from "autosize";
import classNames from "classnames";
import {
  ClipboardEvent,
  Component,
  FormEvent,
  InfernoNode,
  createRef,
} from "inferno";
import { Prompt } from "inferno-router";
import {
  CommunityView,
  CreatePost,
  EditPost,
  GetSiteMetadataResponse,
  Language,
  LanguageId,
  LocalSite,
  MyUserInfo,
  PersonView,
  PostView,
  CommunityTag,
  CommunityTagId,
  UploadImageResponse,
  ModEditPost,
  PagedResponse,
} from "lemmy-js-client";
import {
  archiveTodayUrl,
  ghostArchiveUrl,
  postMarkdownFieldCharacterLimit,
  relTags,
  webArchiveUrl,
} from "@utils/config";
import { PostFormParams } from "@utils/types";
import { I18NextService } from "../../services";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
} from "../../services/HttpService";
import { toast } from "@utils/app";
import { Icon, Spinner } from "../common/icon";
import { LanguageSelect } from "../common/language-select";
import { MarkdownTextArea } from "../common/markdown-textarea";
import { PostListings } from "./post-listings";
import { isBrowser } from "@utils/browser";
import { isMagnetLink, extractMagnetLinkDownloadName } from "@utils/media";
import {
  getUnixTimeLemmy,
  getUnixTime,
  unixTimeToLocalDateStr,
} from "@utils/date";
import { communityTagName } from "@components/community/community-tag";
import { FilterChipSelect } from "@components/common/filter-chip-select";
import { PostName } from "./common";
import { NoOptionI18nKeys } from "i18next";
import { aimooT } from "@utils/aimoo-i18n";

const MAX_POST_TITLE_LENGTH = 200;

interface PostFormProps {
  post_view?: PostView; // If a post is given, that means this is an edit
  crossPosts?: PostView[];
  allLanguages: Language[];
  siteLanguages: number[];
  params?: PostFormParams;
  enableNsfw: boolean;
  showAdultConsentModal: boolean;
  selectedCommunityChoice?: Choice;
  selectedCommunityTags?: CommunityTag[];
  isNsfwCommunity: boolean;
  initialCommunities?: CommunityView[];
  loading: boolean;
  /** Aimoo publish-post layout (create page). */
  aimooLayout?: boolean;
  myUserInfo: MyUserInfo | undefined;
  localSite: LocalSite;
  admins: PersonView[];
  onCancel?: () => void;
  onCreate?: (form: CreatePost, bypassNavWarning: () => void) => void;
  onEdit?: (form: EditPost, bypassNavWarning: () => void) => void;
  onModEdit?: (form: ModEditPost, bypassNavWarning: () => void) => void;
  onSelectCommunity?: (choice: Choice) => void;
  onTitleBlur?: (title: string) => void;
  onUrlBlur?: (url: string) => void;
  onBodyBlur?: (body: string) => void;
  onLanguageChange?: (languageId?: number) => void;
  onNsfwChange?: (nsfw: StringBoolean) => void;
  onThumbnailUrlBlur?: (thumbnailUrl: string) => void;
  onAltTextBlur?: (altText: string) => void;
  onCopySuggestedTitle?: (url: string, title: string) => void;
}

interface PostFormState {
  form: {
    name?: string;
    url?: string;
    body?: string;
    nsfw?: boolean;
    language_id?: LanguageId;
    community_id?: number;
    honeypot?: string;
    custom_thumbnail?: string;
    alt_text?: string;
    tags?: CommunityTagId[];
    // Javascript treats this field as a string, that can't have timezone info.
    scheduled_publish_time_at?: string;
  };
  suggestedPostsRes: RequestState<PagedResponse<PostView>>;
  metadataRes: RequestState<GetSiteMetadataResponse>;
  imageLoading: boolean;
  uploadedImage?: UploadImageResponse;
  communitySearchLoading: boolean;
  communitySearchOptions: Choice[];
  previewMode: boolean;
  bypassNavWarning: boolean;
}

type EditorType = "creator" | "mod_or_admin" | "none";

export class PostForm extends Component<PostFormProps, PostFormState> {
  state: PostFormState = {
    suggestedPostsRes: EMPTY_REQUEST,
    metadataRes: EMPTY_REQUEST,
    form: {},
    imageLoading: false,
    uploadedImage: undefined,
    communitySearchLoading: false,
    previewMode: false,
    communitySearchOptions: [],
    bypassNavWarning: false,
  };

  postTitleRef = createRef<HTMLTextAreaElement>();

  constructor(props: PostFormProps, context: object) {
    super(props, context);

    const { post_view, selectedCommunityChoice, params } = this.props;
    // Means its an edit
    if (post_view) {
      const unix = getUnixTime(post_view.post.scheduled_publish_time_at);
      const scheduled_publish_time_at = unixTimeToLocalDateStr(unix);
      this.state = {
        ...this.state,
        form: {
          body: post_view.post.body,
          name: post_view.post.name,
          community_id: post_view.community.id,
          url: post_view.post.url,
          nsfw: post_view.post.nsfw,
          language_id: post_view.post.language_id,
          custom_thumbnail: post_view.post.thumbnail_url,
          alt_text: post_view.post.alt_text,
          tags: post_view.tags.map(t => t.id),
          scheduled_publish_time_at,
        },
      };
    } else if (selectedCommunityChoice) {
      this.state = {
        ...this.state,
        form: {
          ...this.state.form,
          community_id: getIdFromString(selectedCommunityChoice.value),
        },
        communitySearchOptions: [selectedCommunityChoice].concat(
          (this.props.initialCommunities?.map(communityToChoice) ?? []).filter(
            option => option.value !== selectedCommunityChoice.value,
          ),
        ),
      };
    } else {
      this.state = {
        ...this.state,
        communitySearchOptions:
          this.props.initialCommunities?.map(communityToChoice) ?? [],
      };
    }

    if (params) {
      this.state = {
        ...this.state,
        form: {
          ...this.state.form,
          ...params,
        },
      };
    }
  }

  async componentWillMount() {
    if (this.state.form.url && isBrowser()) {
      await fetchPageTitle(this);
    }
  }

  componentDidMount() {
    if (this.postTitleRef.current) {
      autosize(this.postTitleRef.current);
    }
  }

  componentWillReceiveProps(
    nextProps: Readonly<{ children?: InfernoNode } & PostFormProps>,
  ) {
    if (
      this.props.selectedCommunityChoice?.value !==
        nextProps.selectedCommunityChoice?.value &&
      nextProps.selectedCommunityChoice
    ) {
      this.setState(
        s => (
          (s.form.community_id = getIdFromString(
            nextProps.selectedCommunityChoice?.value,
          )),
          s
        ),
      );
      this.setState({
        communitySearchOptions: [nextProps.selectedCommunityChoice].concat(
          (nextProps.initialCommunities?.map(communityToChoice) ?? []).filter(
            option => option.value !== nextProps.selectedCommunityChoice?.value,
          ),
        ),
      });
    }
    if (
      !this.props.initialCommunities?.length &&
      nextProps.initialCommunities?.length
    ) {
      this.setState({
        communitySearchOptions:
          nextProps.initialCommunities?.map(communityToChoice) ?? [],
      });
    }
    if (this.props.loading && !nextProps.loading) {
      this.setState({ bypassNavWarning: false });
    }
    if (this.props.params !== nextProps.params && nextProps.params) {
      const params = nextProps.params;
      for (const k in params) {
        if (this.props.params?.[k] !== params[k]) {
          this.setState(s => ({
            form: { ...s.form, [k]: params[k] as unknown },
          }));
        }
      }
    }
  }

  get editorType(): EditorType {
    const p = this.props;
    let type_: EditorType;

    if (
      !p.post_view ||
      p.post_view?.creator.id === p.myUserInfo?.local_user_view.person.id
    ) {
      type_ = "creator";
    } else if (p.post_view?.can_mod) {
      type_ = "mod_or_admin";
    } else {
      type_ = "none";
    }
    return type_;
  }

  render() {
    const firstLang = this.state.form.language_id;
    const selectedLangs = firstLang ? Array.of(firstLang) : undefined;

    const url = this.state.form.url;
    const imageUploadDisabled = this.props.localSite.image_upload_disabled;

    return (
      <form
        className={classNames("post-form", {
          "post-form--aimoo": this.props.aimooLayout,
        })}
        onSubmit={e => handlePostSubmit(this, e)}
      >
        <Prompt
          message={I18NextService.i18n.t("block_leaving")}
          when={
            !!(
              this.state.form.name ||
              this.state.form.url ||
              this.state.form.body
            ) && !this.state.bypassNavWarning
          }
        />
        {/* Show an un-editable title for mod edits */}
        {this.editorType === "mod_or_admin" && this.props.post_view && (
          <div className="mb-3">
            <PostName post={this.props.post_view.post} showBody={"preview"} />
          </div>
        )}
        {this.editorType === "creator" && (
          <>
            {!this.props.post_view && (
              <div className="mb-3 row align-items-center post-form__field post-form__field--community">
                <label
                  className="col-sm-2 col-form-label"
                  htmlFor="post-community"
                >
                  {this.props.aimooLayout
                    ? aimooT("publish_to_community")
                    : I18NextService.i18n.t("community")}
                  {this.props.aimooLayout && (
                    <span className="post-form__required">*</span>
                  )}
                </label>
                <div className="col-sm-10">
                  <FilterChipSelect
                    label={"select_a_community"}
                    multiple={false}
                    allOptions={this.state.communitySearchOptions}
                    selectedOptions={
                      this.state.form.community_id
                        ? [this.state.form.community_id.toString()]
                        : []
                    }
                    onSearch={text => handleCommunitySearch(this, text)}
                    onSelect={choices =>
                      handleCommunitySelect(this, choices[0])
                    }
                  />
                </div>
              </div>
            )}
            <div className="mb-3 row post-form__field post-form__field--title">
              <label className="col-sm-2 col-form-label" htmlFor="post-title">
                {I18NextService.i18n.t("title")}
                {this.props.aimooLayout && (
                  <span className="post-form__required">*</span>
                )}
              </label>
              <div className="col-sm-10">
                <textarea
                  value={this.state.form.name}
                  id="post-title"
                  onInput={e => handlePostNameChange(this, e)}
                  className={`form-control ${
                    !validTitle(this.state.form.name) && "is-invalid"
                  }`}
                  required
                  rows={1}
                  minLength={3}
                  maxLength={
                    this.props.aimooLayout ? 80 : MAX_POST_TITLE_LENGTH
                  }
                  placeholder={
                    this.props.aimooLayout
                      ? aimooT("post_title_placeholder")
                      : undefined
                  }
                  ref={this.postTitleRef}
                />
                {this.props.aimooLayout && (
                  <p className="post-form__hint">{aimooT("post_title_hint")}</p>
                )}
                {!validTitle(this.state.form.name) && (
                  <div className="invalid-feedback">
                    {I18NextService.i18n.t("invalid_post_title")}
                  </div>
                )}
                {this.renderSuggestedPosts()}
              </div>
            </div>
            <div className="mb-3 row post-form__field post-form__field--url">
              <label className="col-sm-2 col-form-label" htmlFor="post-url">
                {this.props.aimooLayout
                  ? aimooT("link_label")
                  : I18NextService.i18n.t("url")}
                {this.props.aimooLayout && (
                  <span className="post-form__optional">
                    {aimooT("optional")}
                  </span>
                )}
              </label>
              <div className="col-sm-10">
                <input
                  type="url"
                  placeholder={
                    this.props.aimooLayout
                      ? aimooT("link_placeholder")
                      : I18NextService.i18n.t("optional")
                  }
                  id="post-url"
                  className="form-control mb-3"
                  value={url ?? ""}
                  onInput={e => handlePostUrlChange(this, e)}
                  onPaste={e => handleImageUploadPaste(this, e)}
                />
                {this.props.aimooLayout && (
                  <p className="post-form__hint">{aimooT("link_hint")}</p>
                )}
                {this.renderSuggestedTitleCopy()}
                {/* Show a warning for media posts with missing alt text */}
                {url && isMedia(url) && !this.state.form.alt_text && (
                  <div className="alert alert-warning" role="alert">
                    {I18NextService.i18n.t("missing_alt_text")}
                  </div>
                )}
                {url && validURL(url) && (
                  <div>
                    <a
                      href={`${webArchiveUrl}/save/${encodeURIComponent(url)}`}
                      className="me-2 d-inline-block float-right text-muted small fw-bold"
                      rel={relTags}
                    >
                      archive.org {I18NextService.i18n.t("archive_link")}
                    </a>
                    <a
                      href={`${ghostArchiveUrl}/search${getQueryString({ term: url })}`}
                      className="me-2 d-inline-block float-right text-muted small fw-bold"
                      rel={relTags}
                    >
                      ghostarchive.org {I18NextService.i18n.t("archive_link")}
                    </a>
                    <a
                      href={`${archiveTodayUrl}/${getQueryString({ run: "1", url })}`}
                      className="me-2 d-inline-block float-right text-muted small fw-bold"
                      rel={relTags}
                    >
                      archive.today {I18NextService.i18n.t("archive_link")}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="mb-3 row post-form__field post-form__field--body">
              <label className="col-sm-2 col-form-label">
                {I18NextService.i18n.t("body")}
                {this.props.aimooLayout && (
                  <span className="post-form__required">*</span>
                )}
              </label>
              <div className="col-sm-10">
                {this.props.aimooLayout && (
                  <p className="post-form__field-meta">
                    {aimooT("markdown_supported")}
                  </p>
                )}
                <MarkdownTextArea
                  initialContent={this.state.form.body}
                  placeholder={
                    this.props.aimooLayout
                      ? aimooT("post_body_placeholder")
                      : I18NextService.i18n.t("optional")
                  }
                  onContentChange={val => handlePostBodyChange(this, val)}
                  onContentBlur={val => handlePostBodyBlur(this, val)}
                  allLanguages={this.props.allLanguages}
                  siteLanguages={this.props.siteLanguages}
                  hideNavigationWarnings
                  maxLength={postMarkdownFieldCharacterLimit}
                  myUserInfo={this.props.myUserInfo}
                  imageUploadDisabled={imageUploadDisabled}
                />
              </div>
            </div>
            {this.props.aimooLayout &&
              this.props.selectedCommunityTags &&
              this.props.selectedCommunityTags.length > 0 && (
                <div className="mb-3 row post-form__field post-form__field--tags">
                  <label className="col-sm-2 col-form-label" htmlFor="post-tags">
                    {aimooT("topic_tags")}
                    <span className="post-form__optional">
                      {aimooT("topic_tags_limit")}
                    </span>
                  </label>
                  <div className="col-sm-10">
                    <FilterChipSelect
                      label={"tags"}
                      multiple
                      allOptions={this.props.selectedCommunityTags
                        .filter(t => !t.deleted)
                        .map(tag => {
                          return {
                            value: tag.id.toString(),
                            label: communityTagName(tag),
                          };
                        })}
                      selectedOptions={(this.state.form.tags ?? []).map(t =>
                        t.toString(),
                      )}
                      onSelect={choices => handleTagsChange(this, choices)}
                    />
                  </div>
                </div>
              )}
            <div className="mb-3 row post-form__field post-form__field--image">
              {!this.props.localSite.image_upload_disabled && (
                <>
                  <label
                    htmlFor="file-upload"
                    className={"col-sm-2 col-form-label"}
                  >
                    {this.props.aimooLayout
                      ? aimooT("attachment")
                      : capitalizeFirstLetter(I18NextService.i18n.t("image"))}
                    {this.props.aimooLayout ? (
                      <span className="post-form__optional">
                        {aimooT("optional")}
                      </span>
                    ) : (
                      <Icon icon="image" classes="icon-inline ms-1" />
                    )}
                  </label>
                  <div className="col-sm-10">
                    {this.props.aimooLayout ? (
                      <label
                        htmlFor="file-upload"
                        className="post-form__attachment"
                      >
                        <Icon icon="image" classes="icon-inline" />
                        <span className="post-form__attachment-title">
                          {aimooT("upload_click")}
                        </span>
                        <span className="post-form__attachment-hint">
                          {aimooT("upload_hint")}
                        </span>
                      </label>
                    ) : null}
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/*,video/*"
                      name="file"
                      className={
                        this.props.aimooLayout
                          ? "post-form__attachment-input"
                          : "small col-sm-10 form-control"
                      }
                      disabled={userNotLoggedInOrBanned(this.props.myUserInfo)}
                      onChange={e => handleImageUpload(this, e)}
                    />
                    {this.state.imageLoading && <Spinner />}
                    {url && isImage(url) && (
                      <img src={url} className="img-fluid mt-2" alt="" />
                    )}
                    {this.state.uploadedImage && (
                      <button
                        className="btn btn-danger btn-sm mt-2"
                        onClick={() => handleImageDelete(this)}
                      >
                        <Icon icon="x" classes="icon-inline me-1" />
                        {capitalizeFirstLetter(I18NextService.i18n.t("delete"))}
                      </button>
                    )}
                  </div>
                </>
              )}

              {this.props.crossPosts && this.props.crossPosts.length > 0 && (
                <>
                  <div className="my-1 text-muted small fw-bold">
                    {I18NextService.i18n.t("cross_posts")}
                  </div>
                  <PostListings
                    showCommunity
                    multiCommunity={false}
                    viewOnly
                    showMarkRead="hide"
                    posts={this.props.crossPosts}
                    showCrossPosts="show_separately"
                    enableNsfw={this.props.enableNsfw}
                    showAdultConsentModal={this.props.showAdultConsentModal}
                    allLanguages={this.props.allLanguages}
                    siteLanguages={this.props.siteLanguages}
                    myUserInfo={this.props.myUserInfo}
                    localSite={this.props.localSite}
                    admins={this.props.admins}
                    postListingMode="list"
                    voteLoading={undefined}
                    muteCommunityName={false}
                    mutePersonName
                    hideAvatar
                    onPostEdit={() => {}}
                    onPostModEdit={() => {}}
                    onPostVote={() => {}}
                    onPostReport={() => {}}
                    onBlockPerson={() => {}}
                    onBlockCommunity={() => {}}
                    onLockPost={() => {}}
                    onWarnPost={() => {}}
                    onDeletePost={() => {}}
                    onRemovePost={() => {}}
                    onSavePost={() => {}}
                    onFeaturePost={() => {}}
                    onPurgePerson={() => {}}
                    onPurgePost={() => {}}
                    onBanPersonFromCommunity={() => {}}
                    onBanPerson={() => {}}
                    onAddModToCommunity={() => {}}
                    onAddAdmin={() => {}}
                    onTransferCommunity={() => {}}
                    onMarkPostAsRead={() => {}}
                    onHidePost={() => {}}
                    onPersonNote={() => {}}
                    onScrollIntoCommentsClick={() => {}}
                  />
                </>
              )}
            </div>
            {url && isMedia(url) && (
              <div className="mb-3 row post-form__advanced">
                <label
                  className="col-sm-2 col-form-label"
                  htmlFor="post-alt-text"
                >
                  {I18NextService.i18n.t("column_alttext")}
                </label>
                <div className="col-sm-10">
                  <input
                    autoComplete="false"
                    name="alt_text"
                    placeholder={I18NextService.i18n.t("optional")}
                    type="text"
                    className="form-control mb-3"
                    id="post-alt-text"
                    value={this.state.form.alt_text}
                    onInput={e => handleAltTextChange(this, e)}
                  />
                </div>
              </div>
            )}
            {!isImage(url || "") && (
              <div className="mb-3 row post-form__advanced">
                <label
                  className="col-sm-2 col-form-label"
                  htmlFor="post-custom-thumbnail"
                >
                  {I18NextService.i18n.t("custom_thumbnail_url")}
                </label>
                <div className="col-sm-10">
                  <input
                    type="url"
                    id="post-custom-thumbnail"
                    placeholder={I18NextService.i18n.t("optional")}
                    className="form-control mb-3"
                    value={this.state.form.custom_thumbnail ?? ""}
                    onInput={e => handleCustomThumbnailChange(this, e)}
                  />
                </div>
              </div>
            )}
            <div className="post-form__advanced">
              <LanguageSelect
                allLanguages={this.props.allLanguages}
                siteLanguages={this.props.siteLanguages}
                selectedLanguageIds={selectedLangs}
                multiple={false}
                onChange={val => handleLanguageChange(this, val)}
                myUserInfo={this.props.myUserInfo}
              />
            </div>
          </>
        )}
        {this.props.enableNsfw && !this.props.isNsfwCommunity && (
          <div className="form-check mb-3 post-form__advanced">
            <input
              className="form-check-input"
              id="post-nsfw"
              type="checkbox"
              checked={this.state.form.nsfw}
              onChange={e => handlePostNsfwChange(this, e)}
            />
            <label className="form-check-label" htmlFor="post-nsfw">
              {I18NextService.i18n.t("nsfw")}
            </label>
          </div>
        )}
        {this.editorType === "creator" && !this.props.post_view && (
          <div className="mb-3 row post-form__advanced">
            <label className="col-sm-2 col-form-label" htmlFor="post-schedule">
              {I18NextService.i18n.t("scheduled_publish_time")}
            </label>
            <div className="col-sm-10">
              <input
                type="datetime-local"
                value={this.state.form.scheduled_publish_time_at}
                min={unixTimeToLocalDateStr(Date.now())}
                id="post-schedule"
                className="form-control mb-3"
                onInput={e => handlePostScheduleChange(this, e)}
              />
            </div>
          </div>
        )}
        {!this.props.aimooLayout &&
          this.props.selectedCommunityTags &&
          this.props.selectedCommunityTags.length > 0 && (
            <div className="mb-3 row">
              <label className="col-sm-2 col-form-label" htmlFor="post-tags">
                {I18NextService.i18n.t("tags")}
              </label>
              <div className="col-sm-10">
                <FilterChipSelect
                  label={"tags"}
                  multiple
                  allOptions={this.props.selectedCommunityTags
                    .filter(t => !t.deleted)
                    .map(tag => {
                      return {
                        value: tag.id.toString(),
                        label: communityTagName(tag),
                      };
                    })}
                  selectedOptions={(this.state.form.tags ?? []).map(t =>
                    t.toString(),
                  )}
                  onSelect={choices => handleTagsChange(this, choices)}
                />
              </div>
            </div>
          )}
        <input
          tabIndex={-1}
          autoComplete="false"
          name="a_password"
          type="text"
          className="form-control honeypot"
          id="register-honey"
          value={this.state.form.honeypot}
          onInput={e => handleHoneyPotChange(this, e)}
        />
        <div
          className={classNames("mb-3 row", {
            "post-form__actions": this.props.aimooLayout,
          })}
        >
          <div className="col-sm-10">
            <button
              disabled={!this.state.form.community_id || this.props.loading}
              type="submit"
              className={
                this.props.aimooLayout
                  ? "btn btn-primary post-form__submit"
                  : "btn btn-light border-light-subtle me-2"
              }
            >
              {this.props.loading ? (
                <Spinner />
              ) : this.props.post_view ? (
                capitalizeFirstLetter(I18NextService.i18n.t("save"))
              ) : this.props.aimooLayout ? (
                aimooT("publish_post")
              ) : (
                capitalizeFirstLetter(I18NextService.i18n.t("create"))
              )}
            </button>
            {(this.props.post_view || this.props.aimooLayout) && (
              <button
                type="button"
                className={
                  this.props.aimooLayout
                    ? "btn post-form__cancel"
                    : "btn btn-light border-light-subtle"
                }
                onClick={() => handleCancel(this)}
              >
                {this.props.aimooLayout
                  ? aimooT("cancel")
                  : I18NextService.i18n.t("cancel")}
              </button>
            )}
          </div>
        </div>
      </form>
    );
  }

  renderSuggestedTitleCopy(): InfernoNode | void {
    switch (this.state.metadataRes.state) {
      case "loading":
        return <Spinner />;
      case "success": {
        // Clean up the title of any extra whitespace and replace &nbsp; with a space
        const suggestedTitle = this.state.metadataRes.data.metadata.title
          ?.trim()
          .replace(/\s+/g, " ");
        return (
          suggestedTitle && (
            <button
              type="button"
              className="mt-1 small border-0 bg-transparent p-0 d-block text-muted fw-bold pointer"
              onClick={() => handleCopySuggestedTitle(this, suggestedTitle)}
            >
              {I18NextService.i18n.t("copy_suggested_title", { title: "" })}{" "}
              {suggestedTitle}
            </button>
          )
        );
      }
    }
  }

  renderSuggestedPosts(): InfernoNode | void {
    switch (this.state.suggestedPostsRes.state) {
      case "loading":
        return <Spinner />;
      case "success": {
        const suggestedPosts = this.state.suggestedPostsRes.data.items;

        return (
          suggestedPosts &&
          suggestedPosts.length > 0 && (
            <>
              <div className="my-1 text-muted small fw-bold">
                {I18NextService.i18n.t("related_posts")}
              </div>
              <PostListings
                showCommunity
                multiCommunity={false}
                viewOnly
                showMarkRead="hide"
                posts={suggestedPosts}
                showCrossPosts="show_separately"
                enableNsfw={this.props.enableNsfw}
                showAdultConsentModal={this.props.showAdultConsentModal}
                allLanguages={this.props.allLanguages}
                siteLanguages={this.props.siteLanguages}
                myUserInfo={this.props.myUserInfo}
                localSite={this.props.localSite}
                admins={this.props.admins}
                postListingMode="list"
                voteLoading={undefined}
                muteCommunityName={false}
                mutePersonName
                hideAvatar
                // All of these are unused, since its view only
                onPostEdit={() => {}}
                onPostModEdit={() => {}}
                onPostVote={() => {}}
                onPostReport={() => {}}
                onBlockPerson={() => {}}
                onBlockCommunity={() => {}}
                onLockPost={() => {}}
                onWarnPost={() => {}}
                onDeletePost={() => {}}
                onRemovePost={() => {}}
                onSavePost={() => {}}
                onFeaturePost={() => {}}
                onPurgePerson={() => {}}
                onPurgePost={() => {}}
                onBanPersonFromCommunity={() => {}}
                onBanPerson={() => {}}
                onAddModToCommunity={() => {}}
                onAddAdmin={() => {}}
                onTransferCommunity={() => {}}
                onMarkPostAsRead={() => {}}
                onHidePost={() => {}}
                onPersonNote={() => {}}
                onScrollIntoCommentsClick={() => {}}
              />
            </>
          )
        );
      }
    }
  }
}

const fetchPageTitle = debounce(async (i: PostForm) => {
  const url = i.state.form.url;
  if (url && validURL(url)) {
    // If its a magnet link, fill in the download name
    if (isMagnetLink(url)) {
      const title = extractMagnetLinkDownloadName(url);
      if (title) {
        i.setState({
          metadataRes: {
            state: "success",
            data: {
              metadata: { title },
            },
          },
        });
      }
    } else {
      i.setState({ metadataRes: LOADING_REQUEST });
      i.setState({
        metadataRes: await HttpService.client.getSiteMetadata({ url }),
      });
    }
  }
});

const fetchSimilarPosts = debounce(async (i: PostForm) => {
  const search_term = i.state.form.name;
  if (search_term && search_term !== "") {
    i.setState({ suggestedPostsRes: LOADING_REQUEST });
    i.setState({
      suggestedPostsRes: await HttpService.client.getPosts({
        search_term,
        search_title_only: true,
        limit: 5,
        sort: "top",
        type_: "all",
        community_id: i.state.form.community_id,
      }),
    });
  }
});

function updateUrl(i: PostForm, update: () => void) {
  i.setState({ bypassNavWarning: true });
  update();
  i.setState({ bypassNavWarning: false });
}

function handlePostSubmit(i: PostForm, event: FormEvent<HTMLFormElement>) {
  event.preventDefault();

  const pForm = i.state.form;
  const pv = i.props.post_view;
  const scheduled_publish_time_at = getUnixTimeLemmy(
    pForm.scheduled_publish_time_at,
  );
  // Empty strings fail backend Url::parse (InvalidUrl); omit them.
  const url = emptyToUndefined(pForm.url);
  const custom_thumbnail = emptyToUndefined(pForm.custom_thumbnail);
  const alt_text = emptyToUndefined(pForm.alt_text);
  const body = emptyToUndefined(pForm.body);

  if (pv) {
    if (i.editorType === "creator") {
      i.props.onEdit?.(
        {
          post_id: pv.post.id,
          name: pForm.name,
          url,
          body,
          nsfw: pForm.nsfw,
          language_id: pForm.language_id,
          custom_thumbnail,
          alt_text,
          tags: pForm.tags,
          scheduled_publish_time_at,
        },
        () => {
          i.setState({ bypassNavWarning: true });
        },
      );
    } else if (i.editorType === "mod_or_admin") {
      i.props.onModEdit?.(
        {
          post_id: pv.post.id,
          nsfw: pForm.nsfw,
          tags: pForm.tags,
        },
        () => {
          i.setState({ bypassNavWarning: true });
        },
      );
    }
  } else if (pForm.name && pForm.community_id) {
    i.props.onCreate?.(
      {
        name: pForm.name,
        community_id: pForm.community_id,
        url,
        body,
        nsfw: pForm.nsfw,
        language_id: pForm.language_id,
        honeypot: pForm.honeypot,
        custom_thumbnail,
        alt_text,
        tags: pForm.tags,
        scheduled_publish_time_at,
      },
      () => {
        i.setState({ bypassNavWarning: true });
      },
    );
  }
}

function emptyToUndefined(val?: string): string | undefined {
  const trimmed = val?.trim();
  return trimmed ? trimmed : undefined;
}
function handlePostBodyChange(i: PostForm, val: string) {
  i.setState(s => ((s.form.body = val), s));
}

function handlePostBodyBlur(i: PostForm, val: string) {
  updateUrl(i, () => i.props.onBodyBlur?.(val));
}

function handleLanguageChange(i: PostForm, val: number[]) {
  i.setState(s => ((s.form.language_id = val.at(0)), s));
  updateUrl(i, () => i.props.onLanguageChange?.(val.at(0)));
}

const handleCommunitySearch = debounce(async (i: PostForm, text: string) => {
  const { selectedCommunityChoice } = i.props;
  i.setState({ communitySearchLoading: true });

  const newOptions: Choice[] = [];

  if (selectedCommunityChoice) {
    newOptions.push(selectedCommunityChoice);
  }

  if (text.length > 0) {
    newOptions.push(
      ...filterCommunitySelection(
        await searchCommunities(text),
        i.props.myUserInfo,
      ).map(communityToChoice),
    );

    i.setState({
      communitySearchOptions: newOptions,
    });
  }

  i.setState({
    communitySearchLoading: false,
  });
});

function handleCommunitySelect(i: PostForm, choice: Choice) {
  updateUrl(i, () => i.props.onSelectCommunity?.(choice));
}

async function handleCopySuggestedTitle(i: PostForm, suggestedTitle?: string) {
  if (suggestedTitle) {
    i.setState(
      s => (
        (s.form.name = suggestedTitle?.substring(0, MAX_POST_TITLE_LENGTH)),
        s
      ),
    );
    await fetchSimilarPosts(i);
    i.setState({ suggestedPostsRes: EMPTY_REQUEST });
    setTimeout(() => {
      if (i.postTitleRef.current) {
        autosize.update(i.postTitleRef.current);
      }
    }, 10);

    updateUrl(i, () =>
      i.props.onCopySuggestedTitle?.(i.state.form.url!, suggestedTitle),
    );
  }
}

async function handlePostUrlChange(
  i: PostForm,
  event: FormEvent<HTMLInputElement>,
) {
  const url = emptyToUndefined(event.target.value);

  i.setState(prev => ({
    ...prev,
    form: {
      ...prev.form,
      url,
    },
    uploadedImage: undefined,
  }));

  await fetchPageTitle(i);
}

function handlePostNsfwChange(i: PostForm, event: FormEvent<HTMLInputElement>) {
  i.setState(s => ((s.form.nsfw = event.target.checked), s));

  updateUrl(i, () =>
    i.props.onNsfwChange?.(event.target.checked ? "true" : "false"),
  );
}

function handlePostScheduleChange(
  i: PostForm,
  event: FormEvent<HTMLInputElement>,
) {
  const scheduled_publish_time = event.target.value;

  i.setState(
    s => ((s.form.scheduled_publish_time_at = scheduled_publish_time), s),
  );
}

function handleHoneyPotChange(i: PostForm, event: FormEvent<HTMLInputElement>) {
  i.setState(s => ((s.form.honeypot = event.target.value), s));
}

function handleAltTextChange(i: PostForm, event: FormEvent<HTMLInputElement>) {
  i.setState(s => ((s.form.alt_text = event.target.value), s));
}

function handleCustomThumbnailChange(
  i: PostForm,
  event: FormEvent<HTMLInputElement>,
) {
  i.setState(
    s => ((s.form.custom_thumbnail = emptyToUndefined(event.target.value)), s),
  );
}

function handleCancel(i: PostForm) {
  i.props.onCancel?.();
}

async function handleImageUploadPaste(
  i: PostForm,
  event: ClipboardEvent<HTMLInputElement>,
) {
  const image = event.clipboardData?.files[0];
  if (image) {
    await handleImageUpload(i, image);
  }
}

async function handleImageUpload(
  i: PostForm,
  event: File | FormEvent<HTMLInputElement>,
) {
  let file: File | undefined;
  if (event instanceof Event) {
    event.preventDefault();
    file = event.target.files?.[0];
  } else {
    file = event;
  }

  i.setState({ imageLoading: true });

  if (file) {
    const res = await HttpService.client.uploadImage({ image: file });
    if (res.state === "success") {
      const url = res.data.image_url;
      i.setState(prev => ({
        ...prev,
        form: {
          ...prev.form,
          url,
        },
        uploadedImage: res.data,
      }));
    } else if (res.state === "failed") {
      console.error(res.err.name);
      toast(I18NextService.i18n.t(res.err.name as NoOptionI18nKeys), "danger");
    }
    i.setState({ imageLoading: false });
  }
}

function handleTagsChange(i: PostForm, choices: Choice[]) {
  i.setState(s => ((s.form.tags = choices.map(c => Number(c.value))), s));
}

async function handlePostNameChange(
  i: PostForm,
  event: FormEvent<HTMLTextAreaElement>,
) {
  i.setState(s => ((s.form.name = event.target.value), s));
  await fetchSimilarPosts(i);
}

async function handleImageDelete(i: PostForm) {
  const { uploadedImage } = i.state;

  if (uploadedImage) {
    await HttpService.client.deleteMedia({
      filename: uploadedImage.filename,
    });
  }

  i.setState(prev => ({
    ...prev,
    uploadedImage: undefined,
    imageLoading: false,
    form: {
      ...prev.form,
      url: undefined,
    },
  }));
}
